import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  Shield,
  Building2,
  Users,
  Activity,
  DollarSign,
  Search,
  RefreshCw,
  Eye,
  Edit,
  Trash2,
  UserPlus,
  Key,
  MoreVertical,
  Server,
  Database,
  Cpu,
  CheckCircle,
  XCircle,
  CreditCard,
  TrendingUp,
  Calendar,
  Wallet,
  Receipt,
  ExternalLink,
  AlertTriangle,
  Settings,
  Loader2,
  Mail,
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Tabs, TabsContent } from '../components/ui/tabs';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '../components/ui/dialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  BarChart,
  Bar,
} from 'recharts';
import {
  getAdminHealth,
  getAdminStats,
  getAdminTenants,
  getAdminTenantById,
  updateAdminTenant,
  deleteAdminTenant,
  getAdminTenantUsers,
  addAdminTenantUser,
  deleteAdminUser,
  resetAdminUserPassword,
} from '../services/api';
import { toast } from 'sonner';
import { ADMIN_BASE_URL } from '../config/admin';
import axios from 'axios';

const statusColors = {
  active: 'bg-emerald-500/10 text-emerald-500 border-emerald-500/20',
  suspended: 'bg-amber-500/10 text-amber-500 border-amber-500/20',
  cancelled: 'bg-red-500/10 text-red-500 border-red-500/20',
};

const planColors = {
  professional: 'bg-blue-500/10 text-blue-500 border-blue-500/20',
  enterprise: 'bg-purple-500/10 text-purple-500 border-purple-500/20',
};

const planPrices = {
  professional: 299,
  enterprise: 599,
};

const HealthIndicator = ({ status }) => (
  <div className="flex items-center gap-2">
    {status === 'healthy' ? (
      <CheckCircle className="w-5 h-5 text-emerald-500" />
    ) : (
      <XCircle className="w-5 h-5 text-red-500" />
    )}
    <span className={status === 'healthy' ? 'text-emerald-500' : 'text-red-500'}>
      {status === 'healthy' ? 'All Systems Operational' : 'Issues Detected'}
    </span>
  </div>
);

// Collapsible Stripe Payment Configuration Component
const StripeConfigCollapsible = () => {
  const [stripeConfig, setStripeConfig] = useState(null);
  const [loading, setLoading] = useState(true);
  const [updating, setUpdating] = useState(false);
  const [isExpanded, setIsExpanded] = useState(false);
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const [targetMode, setTargetMode] = useState(null);
  const [confirmText, setConfirmText] = useState('');
  const API = process.env.REACT_APP_BACKEND_URL;

  useEffect(() => {
    fetchStripeConfig();
  }, []);

  const fetchStripeConfig = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API}/api/admin/stripe/config`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setStripeConfig(data);
      }
    } catch (error) {
      console.error('Failed to fetch Stripe config:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleModeChange = async () => {
    const expectedConfirm = `CONFIRM_${targetMode.toUpperCase()}`;
    if (confirmText !== expectedConfirm) {
      toast.error(`Please type "${expectedConfirm}" to confirm`);
      return;
    }

    setUpdating(true);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API}/api/admin/stripe/mode`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({
          mode: targetMode,
          confirmation: confirmText
        })
      });

      if (response.ok) {
        const data = await response.json();
        toast.success(data.message);
        setShowConfirmDialog(false);
        setConfirmText('');
        fetchStripeConfig();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to update Stripe mode');
      }
    } catch (error) {
      toast.error('Failed to update Stripe mode');
    } finally {
      setUpdating(false);
    }
  };

  return (
    <>
      <Card className="bg-slate-900 border-slate-800">
        {/* Collapsible Header */}
        <div 
          className="p-4 cursor-pointer hover:bg-slate-800/50 transition-colors flex items-center justify-between"
          onClick={() => setIsExpanded(!isExpanded)}
        >
          <div className="flex items-center gap-3">
            <CreditCard className="w-5 h-5 text-blue-500" />
            <div>
              <p className="font-medium text-white">Stripe Payment Configuration</p>
              <p className="text-xs text-slate-400">Manage payment processing mode</p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            {loading ? (
              <Loader2 className="w-4 h-4 animate-spin text-slate-400" />
            ) : (
              <Badge className={
                stripeConfig?.is_sandbox 
                  ? 'bg-amber-500/20 text-amber-400 border-amber-500/30' 
                  : 'bg-emerald-500/20 text-emerald-400 border-emerald-500/30'
              }>
                {stripeConfig?.is_sandbox ? 'Sandbox' : 'Live'}
              </Badge>
            )}
            <svg 
              className={`w-5 h-5 text-slate-400 transition-transform ${isExpanded ? 'rotate-180' : ''}`}
              fill="none" 
              stroke="currentColor" 
              viewBox="0 0 24 24"
            >
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
            </svg>
          </div>
        </div>

        {/* Expanded Content */}
        {isExpanded && !loading && (
          <CardContent className="border-t border-slate-800 pt-4 space-y-4">
            {/* Current Mode Status */}
            <div className={`p-4 rounded-lg border ${
              stripeConfig?.is_sandbox 
                ? 'bg-amber-500/10 border-amber-500/30' 
                : 'bg-emerald-500/10 border-emerald-500/30'
            }`}>
              <div className="flex items-center gap-3">
                {stripeConfig?.is_sandbox ? (
                  <AlertTriangle className="w-6 h-6 text-amber-400" />
                ) : (
                  <CheckCircle className="w-6 h-6 text-emerald-400" />
                )}
                <div>
                  <p className={`font-bold ${stripeConfig?.is_sandbox ? 'text-amber-400' : 'text-emerald-400'}`}>
                    {stripeConfig?.is_sandbox ? 'Sandbox Mode' : 'Live Mode'}
                  </p>
                  <p className="text-sm text-slate-400">
                    {stripeConfig?.is_sandbox 
                      ? 'Test mode - No real charges will be made'
                      : 'Production mode - Real payments are processed'
                    }
                  </p>
                </div>
              </div>
            </div>

            {/* Mode Toggle Buttons */}
            <div className="grid grid-cols-2 gap-3">
              <Button
                className={!stripeConfig?.is_sandbox 
                  ? 'bg-emerald-600/50 cursor-not-allowed' 
                  : 'bg-emerald-600 hover:bg-emerald-700'
                }
                disabled={!stripeConfig?.is_sandbox}
                onClick={() => {
                  setTargetMode('live');
                  setShowConfirmDialog(true);
                }}
              >
                <CheckCircle className="w-4 h-4 mr-2" />
                {!stripeConfig?.is_sandbox ? 'Live (Active)' : 'Switch to Live'}
              </Button>
              <Button
                className={stripeConfig?.is_sandbox 
                  ? 'bg-amber-600/50 cursor-not-allowed' 
                  : 'bg-amber-600 hover:bg-amber-700'
                }
                disabled={stripeConfig?.is_sandbox}
                onClick={() => {
                  setTargetMode('sandbox');
                  setShowConfirmDialog(true);
                }}
              >
                <AlertTriangle className="w-4 h-4 mr-2" />
                {stripeConfig?.is_sandbox ? 'Sandbox (Active)' : 'Switch to Sandbox'}
              </Button>
            </div>

            {/* Mode Change History */}
            {stripeConfig?.history && stripeConfig.history.length > 0 && (
              <div className="pt-2">
                <p className="text-sm font-medium text-slate-400 mb-2">Recent Changes</p>
                <div className="space-y-1.5">
                  {stripeConfig.history.slice(0, 3).map((change, idx) => (
                    <div key={idx} className="text-xs flex items-center justify-between text-slate-500">
                      <span>
                        <Badge className={`mr-2 text-xs ${
                          change.new_value === 'sandbox' 
                            ? 'bg-amber-500/20 text-amber-400' 
                            : 'bg-emerald-500/20 text-emerald-400'
                        }`}>
                          {change.old_value} → {change.new_value}
                        </Badge>
                        {change.changed_by_email}
                      </span>
                      <span>{new Date(change.changed_at).toLocaleString()}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </CardContent>
        )}
      </Card>

      {/* Confirmation Dialog */}
      <Dialog open={showConfirmDialog} onOpenChange={setShowConfirmDialog}>
        <DialogContent className="bg-slate-900 border-slate-800">
          <DialogHeader>
            <DialogTitle className="text-white flex items-center gap-2">
              {targetMode === 'sandbox' ? (
                <AlertTriangle className="w-6 h-6 text-amber-400" />
              ) : (
                <CheckCircle className="w-6 h-6 text-emerald-400" />
              )}
              Switch to {targetMode === 'sandbox' ? 'Sandbox' : 'Live'} Mode
            </DialogTitle>
            <DialogDescription className="text-slate-400">
              {targetMode === 'sandbox' 
                ? 'Switching to sandbox mode will process test payments only. No real charges will be made.'
                : 'Switching to live mode will process real payments. Ensure your Stripe account is properly configured.'
              }
            </DialogDescription>
          </DialogHeader>

          <div className={`p-4 rounded-lg border ${
            targetMode === 'sandbox' 
              ? 'bg-amber-500/10 border-amber-500/30' 
              : 'bg-red-500/10 border-red-500/30'
          }`}>
            <p className={`font-medium ${targetMode === 'sandbox' ? 'text-amber-400' : 'text-red-400'}`}>
              {targetMode === 'sandbox' 
                ? '⚠️ Warning: Test Mode'
                : '🔴 Warning: Production Mode'
              }
            </p>
            <p className="text-sm text-slate-400 mt-1">
              {targetMode === 'sandbox' 
                ? 'All new payments will use test Stripe keys. Existing transactions are not affected.'
                : 'All new payments will be real. Make sure your Stripe account can accept payments.'
              }
            </p>
          </div>

          <div className="space-y-2">
            <Label className="text-slate-300">
              Type <code className="bg-slate-800 px-2 py-1 rounded text-xs">CONFIRM_{targetMode?.toUpperCase()}</code> to confirm
            </Label>
            <Input
              value={confirmText}
              onChange={(e) => setConfirmText(e.target.value)}
              placeholder={`CONFIRM_${targetMode?.toUpperCase()}`}
              className="bg-slate-800 border-slate-700 text-white font-mono"
            />
          </div>

          <DialogFooter>
            <Button 
              variant="outline" 
              onClick={() => {
                setShowConfirmDialog(false);
                setConfirmText('');
              }}
              className="border-slate-700"
            >
              Cancel
            </Button>
            <Button 
              className={targetMode === 'sandbox' ? 'bg-amber-600 hover:bg-amber-700' : 'bg-emerald-600 hover:bg-emerald-700'}
              onClick={handleModeChange}
              disabled={updating || confirmText !== `CONFIRM_${targetMode?.toUpperCase()}`}
            >
              {updating && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
              Confirm Switch
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default function GlobalAdmin({ section = 'overview' }) {
  const navigate = useNavigate();
  const [health, setHealth] = useState(null);
  const [stats, setStats] = useState(null);
  const [tenants, setTenants] = useState([]);
  const [totalTenants, setTotalTenants] = useState(0);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  
  // Dialogs
  const [showTenantDialog, setShowTenantDialog] = useState(false);
  const [showUsersDialog, setShowUsersDialog] = useState(false);
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [showAddUserDialog, setShowAddUserDialog] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  
  // Selected items
  const [selectedTenant, setSelectedTenant] = useState(null);
  const [tenantDetails, setTenantDetails] = useState(null);
  const [tenantUsers, setTenantUsers] = useState([]);
  
  // Form states
  const [editForm, setEditForm] = useState({ company_name: '', plan: '', seats_included: 5, status: 'active' });
  const [newUser, setNewUser] = useState({ name: '', email: '', role: 'analyst' });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [healthRes, statsRes, tenantsRes] = await Promise.all([
        getAdminHealth(),
        getAdminStats(),
        getAdminTenants(),
      ]);
      
      // Transform health response
      setHealth({
        status: healthRes.data?.status || 'healthy',
        platform_stats: {
          total_tenants: statsRes.data?.tenants?.total || 0,
          total_users: statsRes.data?.users?.total || 0,
          total_devices: statsRes.data?.devices?.total || 0,
        }
      });
      
      // Transform stats response
      setStats({
        ...statsRes.data,
        trend: statsRes.data?.trend || []
      });
      
      setTenants(tenantsRes.data.tenants);
      setTotalTenants(tenantsRes.data.total || tenantsRes.data.tenants?.length || 0);
    } catch (error) {
      console.error('Failed to fetch admin data:', error);
      toast.error('Failed to load admin data. Make sure you are logged in as super admin.');
    } finally {
      setLoading(false);
    }
  };

  const handleViewTenant = async (tenant) => {
    setSelectedTenant(tenant);
    setShowTenantDialog(true);
    try {
      const response = await getAdminTenantById(tenant.id);
      setTenantDetails(response.data);
    } catch (error) {
      toast.error('Failed to load tenant details');
    }
  };

  const handleViewUsers = async (tenant) => {
    setSelectedTenant(tenant);
    setShowUsersDialog(true);
    try {
      const response = await getAdminTenantUsers(tenant.id);
      setTenantUsers(response.data.users);
    } catch (error) {
      toast.error('Failed to load tenant users');
    }
  };

  const handleEditTenant = (tenant) => {
    setSelectedTenant(tenant);
    setEditForm({
      company_name: tenant.company_name,
      plan: tenant.plan || 'professional',
      seats_included: tenant.seats_included || 5,
      status: tenant.status || 'active',
    });
    setShowEditDialog(true);
  };

  const handleSaveEdit = async () => {
    try {
      await updateAdminTenant(selectedTenant.id, editForm);
      toast.success('Tenant updated successfully');
      setShowEditDialog(false);
      fetchData();
    } catch (error) {
      toast.error('Failed to update tenant');
    }
  };

  const handleDeleteTenant = async () => {
    try {
      await deleteAdminTenant(selectedTenant.id);
      toast.success('Tenant deleted successfully');
      setShowDeleteDialog(false);
      fetchData();
    } catch (error) {
      toast.error('Failed to delete tenant');
    }
  };

  const handleAddUser = async () => {
    if (!newUser.name || !newUser.email) {
      toast.error('Please fill name and email');
      return;
    }
    try {
      const result = await addAdminTenantUser(selectedTenant.id, newUser);
      toast.success(result.data?.invitation_sent 
        ? 'User created and invitation email sent!' 
        : 'User added successfully');
      setShowAddUserDialog(false);
      setNewUser({ name: '', email: '', role: 'analyst' });
      const response = await getAdminTenantUsers(selectedTenant.id);
      setTenantUsers(response.data.users);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to add user');
    }
  };

  const handleDeleteUser = async (userId) => {
    try {
      await deleteAdminUser(userId);
      toast.success('User deleted');
      const response = await getAdminTenantUsers(selectedTenant.id);
      setTenantUsers(response.data.users);
    } catch (error) {
      toast.error('Failed to delete user');
    }
  };

  const handleResetPassword = async (userId) => {
    try {
      const response = await resetAdminUserPassword(userId);
      toast.success(`Password reset. Temporary: ${response.data.temporary_password}`);
    } catch (error) {
      toast.error('Failed to reset password');
    }
  };

  const filteredTenants = tenants.filter((tenant) =>
    tenant.company_name.toLowerCase().includes(searchTerm.toLowerCase())
  );

  // Calculate billing stats
  const totalMRR = tenants.reduce((acc, tenant) => {
    const price = planPrices[tenant.plan] || planPrices.professional;
    return acc + price;
  }, 0);

  const enterpriseCount = tenants.filter(t => t.plan === 'enterprise').length;
  const professionalCount = tenants.filter(t => t.plan === 'professional').length;
  const activeCount = tenants.filter(t => (t.status || 'active') === 'active').length;

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <RefreshCw className="w-8 h-8 text-blue-500 animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="global-admin-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="font-heading font-bold text-3xl text-white flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-red-500/20 flex items-center justify-center">
              <Shield className="w-5 h-5 text-red-500" />
            </div>
            System Administration
          </h1>
          <p className="text-slate-400 mt-1">Manage tenants, billing, users, and system health</p>
        </div>
        <div className="flex items-center gap-2">
          <HealthIndicator status={health?.status} />
          <Button variant="outline" onClick={fetchData} className="border-slate-700">
            <RefreshCw className="w-4 h-4 mr-2" />
            Refresh
          </Button>
        </div>
      </div>

      <Tabs value={section} className="w-full">
        {/* Tab content - navigation is in the sidebar */}

        {/* Overview Tab */}
        <TabsContent value="overview" className="space-y-6 mt-2">
          {/* Key Metrics */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <Card className="bg-slate-900 border-slate-800">
              <CardContent className="p-4">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-lg bg-blue-500/10 flex items-center justify-center">
                    <Building2 className="w-5 h-5 text-blue-500" />
                  </div>
                  <div>
                    <p className="text-2xl font-heading font-bold text-white">
                      {health?.platform_stats?.total_tenants || 0}
                    </p>
                    <p className="text-xs text-slate-500">Total Tenants</p>
                  </div>
                </div>
              </CardContent>
            </Card>
            <Card className="bg-slate-900 border-slate-800">
              <CardContent className="p-4">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-lg bg-purple-500/10 flex items-center justify-center">
                    <Users className="w-5 h-5 text-purple-500" />
                  </div>
                  <div>
                    <p className="text-2xl font-heading font-bold text-white">
                      {health?.platform_stats?.total_users || 0}
                    </p>
                    <p className="text-xs text-slate-500">Total Users</p>
                  </div>
                </div>
              </CardContent>
            </Card>
            <Card className="bg-slate-900 border-slate-800">
              <CardContent className="p-4">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-lg bg-emerald-500/10 flex items-center justify-center">
                    <DollarSign className="w-5 h-5 text-emerald-500" />
                  </div>
                  <div>
                    <p className="text-2xl font-heading font-bold text-white">
                      ${totalMRR.toLocaleString()}
                    </p>
                    <p className="text-xs text-slate-500">Monthly Revenue</p>
                  </div>
                </div>
              </CardContent>
            </Card>
            <Card className="bg-slate-900 border-slate-800">
              <CardContent className="p-4">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-lg bg-amber-500/10 flex items-center justify-center">
                    <CheckCircle className="w-5 h-5 text-amber-500" />
                  </div>
                  <div>
                    <p className="text-2xl font-heading font-bold text-white">
                      {activeCount}
                    </p>
                    <p className="text-xs text-slate-500">Active Subscriptions</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Plan Distribution */}
            <Card className="bg-slate-900 border-slate-800">
              <CardHeader>
                <CardTitle className="text-lg font-heading text-white">Subscription Plans</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  <div className="flex items-center justify-between p-4 rounded-lg bg-slate-800/50">
                    <div className="flex items-center gap-3">
                      <div className="w-3 h-3 rounded-full bg-blue-500" />
                      <span className="text-slate-300">Professional</span>
                    </div>
                    <div className="text-right">
                      <span className="text-xl font-heading font-bold text-white">{professionalCount}</span>
                      <span className="text-slate-500 ml-2">tenants</span>
                    </div>
                  </div>
                  <div className="flex items-center justify-between p-4 rounded-lg bg-slate-800/50">
                    <div className="flex items-center gap-3">
                      <div className="w-3 h-3 rounded-full bg-purple-500" />
                      <span className="text-slate-300">Enterprise</span>
                    </div>
                    <div className="text-right">
                      <span className="text-xl font-heading font-bold text-white">{enterpriseCount}</span>
                      <span className="text-slate-500 ml-2">tenants</span>
                    </div>
                  </div>
                  <div className="pt-4 border-t border-slate-800">
                    <div className="flex items-center justify-between">
                      <span className="text-slate-400">Average Revenue per Tenant</span>
                      <span className="text-lg font-bold text-emerald-500">
                        ${tenants.length > 0 ? Math.round(totalMRR / tenants.length) : 0}/mo
                      </span>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Recent Signups */}
            <Card className="bg-slate-900 border-slate-800">
              <CardHeader>
                <CardTitle className="text-lg font-heading text-white">Recent Signups</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-2">
                  {health?.recent_signups?.slice(0, 5).map((tenant, index) => (
                    <div key={index} className="flex items-center justify-between p-3 rounded-lg bg-slate-800/50">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-lg bg-blue-500/10 flex items-center justify-center">
                          <Building2 className="w-5 h-5 text-blue-500" />
                        </div>
                        <div>
                          <p className="font-medium text-white">{tenant.company_name}</p>
                          <p className="text-xs text-slate-500">
                            {new Date(tenant.created_at).toLocaleDateString()}
                          </p>
                        </div>
                      </div>
                      <Badge className={planColors[tenant.plan] || planColors.professional}>
                        {tenant.plan || 'professional'}
                      </Badge>
                    </div>
                  ))}
                  {(!health?.recent_signups || health.recent_signups.length === 0) && (
                    <p className="text-center py-4 text-slate-500">No recent signups</p>
                  )}
                </div>
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        {/* Tenants Tab */}
        <TabsContent value="tenants" className="space-y-6 mt-2">
          <div className="flex gap-2 mb-4">
            <div className="relative flex-1 max-w-sm">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" />
              <Input
                placeholder="Search tenants..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-9 bg-slate-900 border-slate-800 text-white"
              />
            </div>
          </div>

          <Card className="bg-slate-900 border-slate-800">
            <CardContent className="p-0">
              <Table>
                <TableHeader>
                  <TableRow className="border-slate-800 hover:bg-transparent">
                    <TableHead className="text-slate-400">Company</TableHead>
                    <TableHead className="text-slate-400">Plan</TableHead>
                    <TableHead className="text-slate-400">Users</TableHead>
                    <TableHead className="text-slate-400">Seats</TableHead>
                    <TableHead className="text-slate-400">MRR</TableHead>
                    <TableHead className="text-slate-400">Status</TableHead>
                    <TableHead className="text-slate-400">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredTenants.map((tenant) => (
                    <TableRow 
                      key={tenant.id} 
                      className="border-slate-800 hover:bg-slate-800/50 cursor-pointer"
                      onClick={() => navigate(`${ADMIN_BASE_URL}/tenants/${tenant.id}`)}
                    >
                      <TableCell>
                        <div>
                          <p className="font-medium text-white">{tenant.company_name}</p>
                          <p className="text-xs text-slate-500">{tenant.id.slice(0, 8)}...</p>
                        </div>
                      </TableCell>
                      <TableCell>
                        <Badge className={planColors[tenant.plan] || planColors.professional}>
                          {tenant.plan || 'professional'}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-slate-300">{tenant.users_count || 0}</TableCell>
                      <TableCell className="text-slate-300">
                        {tenant.seats_used || tenant.users_count || 0} / {tenant.seats_included || 5}
                      </TableCell>
                      <TableCell className="text-emerald-500 font-medium">
                        ${planPrices[tenant.plan] || planPrices.professional}
                      </TableCell>
                      <TableCell>
                        <Badge className={statusColors[tenant.status || 'active']}>
                          {tenant.status || 'active'}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        <Button 
                          variant="ghost" 
                          size="sm"
                          onClick={(e) => {
                            e.stopPropagation();
                            navigate(`${ADMIN_BASE_URL}/tenants/${tenant.id}`);
                          }}
                          className="text-blue-400 hover:text-blue-300"
                        >
                          <ExternalLink className="w-4 h-4 mr-1" />
                          View
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Billing Tab */}
        <TabsContent value="billing" className="space-y-6 mt-2">
          {/* Revenue Stats */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <Card className="bg-slate-900 border-slate-800">
              <CardContent className="p-4">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-lg bg-emerald-500/10 flex items-center justify-center">
                    <Wallet className="w-5 h-5 text-emerald-500" />
                  </div>
                  <div>
                    <p className="text-2xl font-heading font-bold text-white">
                      ${totalMRR.toLocaleString()}
                    </p>
                    <p className="text-xs text-slate-500">Monthly Recurring Revenue</p>
                  </div>
                </div>
              </CardContent>
            </Card>
            <Card className="bg-slate-900 border-slate-800">
              <CardContent className="p-4">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-lg bg-blue-500/10 flex items-center justify-center">
                    <TrendingUp className="w-5 h-5 text-blue-500" />
                  </div>
                  <div>
                    <p className="text-2xl font-heading font-bold text-white">
                      ${(totalMRR * 12).toLocaleString()}
                    </p>
                    <p className="text-xs text-slate-500">Annual Run Rate</p>
                  </div>
                </div>
              </CardContent>
            </Card>
            <Card className="bg-slate-900 border-slate-800">
              <CardContent className="p-4">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-lg bg-purple-500/10 flex items-center justify-center">
                    <Receipt className="w-5 h-5 text-purple-500" />
                  </div>
                  <div>
                    <p className="text-2xl font-heading font-bold text-white">
                      {enterpriseCount}
                    </p>
                    <p className="text-xs text-slate-500">Enterprise Plans</p>
                  </div>
                </div>
              </CardContent>
            </Card>
            <Card className="bg-slate-900 border-slate-800">
              <CardContent className="p-4">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-lg bg-amber-500/10 flex items-center justify-center">
                    <Calendar className="w-5 h-5 text-amber-500" />
                  </div>
                  <div>
                    <p className="text-2xl font-heading font-bold text-white">
                      {professionalCount}
                    </p>
                    <p className="text-xs text-slate-500">Professional Plans</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Revenue by Plan */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <Card className="bg-slate-900 border-slate-800">
              <CardHeader>
                <CardTitle className="text-lg font-heading text-white">Revenue by Plan</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  <div className="p-4 rounded-lg bg-blue-500/10 border border-blue-500/20">
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-blue-400 font-medium">Professional Plan</span>
                      <span className="text-white font-bold">
                        ${(professionalCount * planPrices.professional).toLocaleString()}/mo
                      </span>
                    </div>
                    <div className="text-sm text-slate-400">
                      {professionalCount} tenants × $299/month
                    </div>
                  </div>
                  <div className="p-4 rounded-lg bg-purple-500/10 border border-purple-500/20">
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-purple-400 font-medium">Enterprise Plan</span>
                      <span className="text-white font-bold">
                        ${(enterpriseCount * planPrices.enterprise).toLocaleString()}/mo
                      </span>
                    </div>
                    <div className="text-sm text-slate-400">
                      {enterpriseCount} tenants × $599/month
                    </div>
                  </div>
                  <div className="pt-4 border-t border-slate-800">
                    <div className="flex items-center justify-between">
                      <span className="text-slate-300 font-medium">Total MRR</span>
                      <span className="text-2xl font-heading font-bold text-emerald-500">
                        ${totalMRR.toLocaleString()}
                      </span>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card className="bg-slate-900 border-slate-800">
              <CardHeader>
                <CardTitle className="text-lg font-heading text-white">Seat Usage</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  {filteredTenants.slice(0, 5).map((tenant) => {
                    const seatsUsed = tenant.seats_used || tenant.users_count || 0;
                    const seatsIncluded = tenant.seats_included || 5;
                    const percentage = Math.min((seatsUsed / seatsIncluded) * 100, 100);
                    
                    return (
                      <div key={tenant.id}>
                        <div className="flex items-center justify-between mb-1">
                          <span className="text-sm text-slate-300">{tenant.company_name}</span>
                          <span className="text-xs text-slate-500">
                            {seatsUsed} / {seatsIncluded} seats
                          </span>
                        </div>
                        <div className="h-2 bg-slate-800 rounded-full overflow-hidden">
                          <div 
                            className={`h-full rounded-full transition-all ${percentage >= 90 ? 'bg-red-500' : percentage >= 70 ? 'bg-amber-500' : 'bg-emerald-500'}`}
                            style={{ width: `${percentage}%` }}
                          />
                        </div>
                      </div>
                    );
                  })}
                </div>
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        {/* System Health Tab */}
        <TabsContent value="health" className="space-y-6 mt-2">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <Card className="bg-slate-900 border-slate-800">
              <CardHeader>
                <CardTitle className="text-lg font-heading text-white flex items-center gap-2">
                  <Server className="w-5 h-5 text-blue-500" />
                  API Server
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="flex items-center gap-2 mb-4">
                  <CheckCircle className="w-6 h-6 text-emerald-500" />
                  <span className="text-emerald-500 font-medium">Operational</span>
                </div>
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between">
                    <span className="text-slate-500">Status</span>
                    <span className="text-white">{health?.status || 'healthy'}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-slate-500">Last Check</span>
                    <span className="text-white">
                      {health?.timestamp ? new Date(health.timestamp).toLocaleTimeString() : 'N/A'}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-slate-500">Uptime</span>
                    <span className="text-emerald-500">99.99%</span>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card className="bg-slate-900 border-slate-800">
              <CardHeader>
                <CardTitle className="text-lg font-heading text-white flex items-center gap-2">
                  <Database className="w-5 h-5 text-blue-500" />
                  Database
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="flex items-center gap-2 mb-4">
                  <CheckCircle className="w-6 h-6 text-emerald-500" />
                  <span className="text-emerald-500 font-medium">Connected</span>
                </div>
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between">
                    <span className="text-slate-500">Type</span>
                    <span className="text-white">MongoDB</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-slate-500">Collections</span>
                    <span className="text-white">12</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-slate-500">Status</span>
                    <span className="text-emerald-500">Healthy</span>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card className="bg-slate-900 border-slate-800">
              <CardHeader>
                <CardTitle className="text-lg font-heading text-white flex items-center gap-2">
                  <Cpu className="w-5 h-5 text-blue-500" />
                  AI Engine
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="flex items-center gap-2 mb-4">
                  <CheckCircle className="w-6 h-6 text-emerald-500" />
                  <span className="text-emerald-500 font-medium">Active</span>
                </div>
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between">
                    <span className="text-slate-500">Provider</span>
                    <span className="text-white">OpenAI</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-slate-500">Model</span>
                    <span className="text-white">GPT-5.1</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-slate-500">Status</span>
                    <span className="text-emerald-500">Online</span>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

          <Card className="bg-slate-900 border-slate-800">
            <CardHeader>
              <CardTitle className="text-lg font-heading text-white">Service Status</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <div className="p-4 rounded-lg bg-slate-800/50 flex items-center gap-3">
                  <CheckCircle className="w-5 h-5 text-emerald-500" />
                  <div>
                    <p className="text-white font-medium">Authentication</p>
                    <p className="text-xs text-slate-500">Operational</p>
                  </div>
                </div>
                <div className="p-4 rounded-lg bg-slate-800/50 flex items-center gap-3">
                  <CheckCircle className="w-5 h-5 text-emerald-500" />
                  <div>
                    <p className="text-white font-medium">Billing Service</p>
                    <p className="text-xs text-slate-500">Operational</p>
                  </div>
                </div>
                <div className="p-4 rounded-lg bg-slate-800/50 flex items-center gap-3">
                  <CheckCircle className="w-5 h-5 text-emerald-500" />
                  <div>
                    <p className="text-white font-medium">Email Service</p>
                    <p className="text-xs text-slate-500">Operational</p>
                  </div>
                </div>
                <div className="p-4 rounded-lg bg-slate-800/50 flex items-center gap-3">
                  <CheckCircle className="w-5 h-5 text-emerald-500" />
                  <div>
                    <p className="text-white font-medium">Agent Updates</p>
                    <p className="text-xs text-slate-500">Operational</p>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Stripe Payment Configuration - Collapsible */}
          <StripeConfigCollapsible />
        </TabsContent>
      </Tabs>

      {/* Tenant Details Dialog */}
      <Dialog open={showTenantDialog} onOpenChange={setShowTenantDialog}>
        <DialogContent className="bg-slate-900 border-slate-800 max-w-2xl">
          <DialogHeader>
            <DialogTitle className="text-white">{selectedTenant?.company_name}</DialogTitle>
            <DialogDescription className="text-slate-400">Tenant details and subscription info</DialogDescription>
          </DialogHeader>
          {tenantDetails ? (
            <div className="space-y-4">
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <div className="p-3 rounded-lg bg-slate-800/50 text-center">
                  <p className="text-xl font-bold text-white">{tenantDetails.stats?.users_count || 0}</p>
                  <p className="text-xs text-slate-500">Users</p>
                </div>
                <div className="p-3 rounded-lg bg-slate-800/50 text-center">
                  <p className="text-xl font-bold text-white">{tenantDetails.stats?.devices_count || 0}</p>
                  <p className="text-xs text-slate-500">Devices</p>
                </div>
                <div className="p-3 rounded-lg bg-slate-800/50 text-center">
                  <p className="text-xl font-bold text-white">
                    {tenantDetails.tenant?.seats_used || 0} / {tenantDetails.tenant?.seats_included || 5}
                  </p>
                  <p className="text-xs text-slate-500">Seats</p>
                </div>
                <div className="p-3 rounded-lg bg-emerald-500/10 text-center">
                  <p className="text-xl font-bold text-emerald-500">
                    ${planPrices[tenantDetails.tenant?.plan] || 299}
                  </p>
                  <p className="text-xs text-slate-500">Monthly</p>
                </div>
              </div>
              <div>
                <p className="text-sm text-slate-400 mb-2">Subscription Info</p>
                <div className="p-3 rounded-lg bg-slate-800/50 text-sm space-y-1">
                  <div className="flex justify-between">
                    <span className="text-slate-500">Tenant ID</span>
                    <span className="text-white font-mono text-xs">{tenantDetails.tenant?.id}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-slate-500">Plan</span>
                    <Badge className={planColors[tenantDetails.tenant?.plan] || planColors.professional}>
                      {tenantDetails.tenant?.plan || 'professional'}
                    </Badge>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-slate-500">Status</span>
                    <Badge className={statusColors[tenantDetails.tenant?.status || 'active']}>
                      {tenantDetails.tenant?.status || 'active'}
                    </Badge>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-slate-500">Created</span>
                    <span className="text-white">{new Date(tenantDetails.tenant?.created_at).toLocaleDateString()}</span>
                  </div>
                </div>
              </div>
            </div>
          ) : (
            <div className="flex items-center justify-center py-8">
              <RefreshCw className="w-6 h-6 text-slate-500 animate-spin" />
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Users Dialog */}
      <Dialog open={showUsersDialog} onOpenChange={setShowUsersDialog}>
        <DialogContent className="bg-slate-900 border-slate-800 max-w-2xl">
          <DialogHeader>
            <DialogTitle className="text-white flex items-center justify-between">
              <span>{selectedTenant?.company_name} - Users</span>
              <Button
                size="sm"
                onClick={() => setShowAddUserDialog(true)}
                className="bg-blue-600 hover:bg-blue-700"
              >
                <UserPlus className="w-4 h-4 mr-1" />
                Add User
              </Button>
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-2 max-h-96 overflow-y-auto">
            {tenantUsers.map((user) => (
              <div key={user.id} className="flex items-center justify-between p-3 rounded-lg bg-slate-800/50">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full bg-blue-500/20 flex items-center justify-center">
                    <span className="text-sm font-medium text-blue-500">
                      {user.name?.charAt(0).toUpperCase()}
                    </span>
                  </div>
                  <div>
                    <p className="font-medium text-white">{user.name}</p>
                    <p className="text-xs text-slate-500">{user.email}</p>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <Badge className="bg-slate-700 text-slate-300">{user.role}</Badge>
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={() => handleResetPassword(user.id)}
                    className="text-amber-500 hover:text-amber-400 hover:bg-amber-500/10"
                    title="Reset Password"
                  >
                    <Key className="w-4 h-4" />
                  </Button>
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={() => handleDeleteUser(user.id)}
                    className="text-red-500 hover:text-red-400 hover:bg-red-500/10"
                    title="Delete User"
                  >
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
              </div>
            ))}
            {tenantUsers.length === 0 && (
              <p className="text-center py-4 text-slate-500">No users found</p>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Add User Dialog */}
      <Dialog open={showAddUserDialog} onOpenChange={setShowAddUserDialog}>
        <DialogContent className="bg-slate-900 border-slate-800">
          <DialogHeader>
            <DialogTitle className="text-white">Add User to {selectedTenant?.company_name}</DialogTitle>
            <DialogDescription className="text-slate-400">
              The user will receive a welcome email with instructions to set their password and complete onboarding.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label className="text-slate-300">Name</Label>
              <Input
                value={newUser.name}
                onChange={(e) => setNewUser({ ...newUser, name: e.target.value })}
                placeholder="John Doe"
                className="bg-slate-800 border-slate-700 text-white"
              />
            </div>
            <div className="space-y-2">
              <Label className="text-slate-300">Email</Label>
              <Input
                type="email"
                value={newUser.email}
                onChange={(e) => setNewUser({ ...newUser, email: e.target.value })}
                placeholder="john@company.com"
                className="bg-slate-800 border-slate-700 text-white"
              />
            </div>
            <div className="space-y-2">
              <Label className="text-slate-300">Role</Label>
              <Select value={newUser.role} onValueChange={(v) => setNewUser({ ...newUser, role: v })}>
                <SelectTrigger className="bg-slate-800 border-slate-700 text-white">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-slate-800 border-slate-700">
                  <SelectItem value="admin">Admin</SelectItem>
                  <SelectItem value="analyst">Analyst</SelectItem>
                  <SelectItem value="read-only">Read Only</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAddUserDialog(false)} className="border-slate-700">
              Cancel
            </Button>
            <Button onClick={handleAddUser} className="bg-blue-600 hover:bg-blue-700">
              <Mail className="w-4 h-4 mr-2" />
              Send Invitation
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Tenant Dialog */}
      <Dialog open={showEditDialog} onOpenChange={setShowEditDialog}>
        <DialogContent className="bg-slate-900 border-slate-800">
          <DialogHeader>
            <DialogTitle className="text-white">Edit Subscription</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label className="text-slate-300">Company Name</Label>
              <Input
                value={editForm.company_name}
                onChange={(e) => setEditForm({ ...editForm, company_name: e.target.value })}
                className="bg-slate-800 border-slate-700 text-white"
              />
            </div>
            <div className="space-y-2">
              <Label className="text-slate-300">Plan</Label>
              <Select value={editForm.plan} onValueChange={(v) => setEditForm({ ...editForm, plan: v })}>
                <SelectTrigger className="bg-slate-800 border-slate-700 text-white">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-slate-800 border-slate-700">
                  <SelectItem value="professional">Professional ($299/mo)</SelectItem>
                  <SelectItem value="enterprise">Enterprise ($599/mo)</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label className="text-slate-300">Seats Included</Label>
              <Input
                type="number"
                value={editForm.seats_included}
                onChange={(e) => setEditForm({ ...editForm, seats_included: parseInt(e.target.value) })}
                className="bg-slate-800 border-slate-700 text-white"
              />
            </div>
            <div className="space-y-2">
              <Label className="text-slate-300">Status</Label>
              <Select value={editForm.status} onValueChange={(v) => setEditForm({ ...editForm, status: v })}>
                <SelectTrigger className="bg-slate-800 border-slate-700 text-white">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-slate-800 border-slate-700">
                  <SelectItem value="active">Active</SelectItem>
                  <SelectItem value="suspended">Suspended</SelectItem>
                  <SelectItem value="cancelled">Cancelled</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowEditDialog(false)} className="border-slate-700">
              Cancel
            </Button>
            <Button onClick={handleSaveEdit} className="bg-blue-600 hover:bg-blue-700">
              Save Changes
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <DialogContent className="bg-slate-900 border-slate-800">
          <DialogHeader>
            <DialogTitle className="text-white">Delete Tenant</DialogTitle>
            <DialogDescription className="text-slate-400">
              Are you sure you want to delete <strong>{selectedTenant?.company_name}</strong>? This will permanently remove all associated users and data. This action cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDeleteDialog(false)} className="border-slate-700">
              Cancel
            </Button>
            <Button onClick={handleDeleteTenant} className="bg-red-600 hover:bg-red-700">
              Delete Tenant
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
